// One "Routine cleanup" card for every routine group Claude proposed in this round, instead of
// one card per group. The reader sees one decision: N routine threads, what kinds, Look first (every
// thread and why), and Archive all N (arm → confirm; each group still gets its own digest-bound
// approval in the gateway, confirmed one after another). Focus mode keeps RoutineGroupBlock.
import { memo, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { toast } from "sonner";
import type { RoutineGroupView } from "@shared/api";
import { cn } from "@web/lib/cn";
import { feedApi } from "@web/lib/feedApi";
import { invalidateBoards } from "@web/lib/feedQueries";
import { registerUndo, undoWindowMs } from "@web/lib/undoRegistry";
import { useCommand } from "@web/shell/commands";
import { Button } from "@web/ui/Button";
import { Kbd } from "@web/ui/Kbd";
import { CONFIRM_GUARD_MS } from "./actions";
import { cancelWork } from "./cardOps";
import {
  cleanupCount,
  cleanupLabel,
  cleanupLabels,
  cleanupSummary,
  cleanupTitle,
} from "./cleanupWords";
import { routineArmKey } from "./routineArm";
import { RoutineItemRow, routineApproveFailure, threadsWord } from "./RoutineGroupBlock";
import { routineNavId } from "./selectors";

/** The j/k list id of the combined cleanup entry (one per board). */
export const CLEANUP_NAV_ID = routineNavId("cleanup");
export const CLEANUP_GROUP_ID = "cleanup";

export interface CleanupBlockProps {
  /** Proposed groups of this round, in server order. */
  groups: readonly RoutineGroupView[];
  /** DOM id for scrolling and aria-activedescendant (the list column). */
  id?: string;
  selected?: boolean;
  /** Keys (1 arms, 1 or Enter confirms, Esc disarms) work only on the active block. */
  active?: boolean;
  /** Show the threads without a click (the board's side pane). */
  defaultOpen?: boolean;
  onSelect?: () => void;
}

export interface CleanupRowProps {
  groups: readonly RoutineGroupView[];
  id: string;
  selected: boolean;
  onSelect: () => void;
}

/** What an arming is bound to: every group's revision and exact threads. */
const cleanupArmKey = (groups: readonly RoutineGroupView[]) =>
  groups.map((g) => `${g.id}\n${routineArmKey(g)}`).join("\n--\n");

/** The full card (side pane, narrow screens). */
export function CleanupBlock({
  groups,
  id,
  selected = false,
  active = false,
  defaultOpen = false,
  onSelect,
}: CleanupBlockProps) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(defaultOpen);
  const [left, setLeft] = useState<ReadonlySet<string>>(new Set());
  const [armedAt, setArmedAt] = useState<number | null>(null);
  const [progress, setProgress] = useState<{ at: number; of: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const confirmRef = useRef<HTMLButtonElement>(null);

  const proposedGroups = groups.filter((g) => g.status === "proposed");
  const included = (g: RoutineGroupView) => g.items.filter((i) => !left.has(i.threadKey));
  const count = proposedGroups.reduce((sum, g) => sum + included(g).length, 0);
  const canAct = count > 0;

  // The groups changing under an armed cleanup (more mail folded in, a group gone) disarms it: a
  // confirm covers only what was armed. Approving group by group changes them too; that is ours.
  const armKey = cleanupArmKey(groups);
  const [armedKey, setArmedKey] = useState<string | null>(null);
  useEffect(() => {
    if (busyRef.current) return;
    if (armedAt && armedKey !== armKey) {
      setArmedAt(null);
      toast("This cleanup changed. Look again before confirming.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armKey]);

  useEffect(() => {
    if (!armedAt) return;
    confirmRef.current?.focus({ preventScroll: true });
    confirmRef.current
      ?.closest("[role=group]")
      ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [armedAt]);

  // Leaving the block (j/k elsewhere) disarms it: what is confirmed must be on screen.
  const [wasActive, setWasActive] = useState(active);
  if (wasActive !== active) {
    setWasActive(active);
    if (!active) setArmedAt(null);
  }

  const arm = () => {
    if (!canAct || busy) return;
    setOpen(true);
    setArmedKey(armKey);
    setArmedAt(Date.now());
  };

  const approve = async () => {
    if (!armedAt || Date.now() - armedAt < CONFIRM_GUARD_MS || !canAct || busy) return;
    if (armedKey !== armKey) {
      setArmedAt(null);
      return;
    }
    const todo = proposedGroups
      .map((g) => ({ group: g, keys: included(g).map((i) => i.threadKey) }))
      .filter((t) => t.keys.length > 0);
    busyRef.current = true;
    setBusy(true);
    const works: { id: string; windowMs: number | null }[] = [];
    let archived = 0;
    try {
      for (const [index, { group, keys }] of todo.entries()) {
        setProgress({ at: index + 1, of: todo.length });
        const result = await feedApi.approveRoutine(group.id, keys, group.itemsRev);
        works.push({
          id: result.work.id,
          windowMs: undoWindowMs(result.undoUntil, Date.now()),
        });
        archived += keys.length;
      }
    } catch (error) {
      routineApproveFailure(error);
    } finally {
      announceArchiving(archived, works);
      busyRef.current = false;
      setBusy(false);
      setProgress(null);
      setArmedAt(null);
      invalidateBoards(queryClient, groups[0]?.feedId);
    }
  };

  /** One Undo covers every group approved in this press. */
  const announceArchiving = (n: number, works: { id: string; windowMs: number | null }[]) => {
    if (!works.length) return;
    const windows = works.flatMap((w) => (w.windowMs ? [w.windowMs] : []));
    if (windows.length === works.length) {
      registerUndo({
        message: `Archiving ${threadsWord(n)}…`,
        durationMs: Math.min(...windows),
        undo: () => Promise.allSettled(works.map((w) => cancelWork(queryClient, w.id))),
        doneMessage: "Cancelled. Nothing changed in Gmail.",
      });
    } else {
      toast(`Archiving ${threadsWord(n)}…`);
    }
  };

  const keysOn = active && canAct;
  useCommand("card.action1", () => (armedAt ? void approve() : arm()), keysOn);
  useCommand("card.confirm", () => void approve(), keysOn && !!armedAt);
  useCommand("card.cancel", () => setArmedAt(null), keysOn && !!armedAt);

  const toggleItem = (threadKey: string) => {
    setArmedAt(null);
    setLeft((prev) => {
      const next = new Set(prev);
      if (next.has(threadKey)) next.delete(threadKey);
      else next.add(threadKey);
      return next;
    });
  };

  if (groups.length === 0) return null;

  const total = cleanupCount(groups);
  const title = cleanupTitle(total);
  const summary = cleanupSummary(groups);
  const headed = cleanupLabels(groups).length > 1;

  return (
    <article
      id={id}
      aria-label={`Routine cleanup: ${title}`}
      aria-current={selected || undefined}
      data-routine-group={CLEANUP_GROUP_ID}
      onClick={onSelect}
      className={cn(
        "relative rounded-xl border bg-memo/60 px-4 py-3",
        selected ? "border-accent/60 ring-1 ring-accent/40" : "border-line",
      )}
      onKeyDown={(e) => {
        if (e.key === "Escape" && armedAt) {
          e.preventDefault();
          e.stopPropagation();
          setArmedAt(null);
        }
      }}
    >
      {selected ? (
        <span aria-hidden className="absolute inset-y-3 left-0 w-[3px] rounded-r bg-accent" />
      ) : null}
      <div className="flex flex-col gap-2">
        <div className="min-w-0">
          <p className="mm-kicker">Routine cleanup</p>
          <h3 className="text-lg leading-snug">{title}</h3>
          {summary ? <p className="text-sm text-ink-2">{summary}</p> : null}
        </div>
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1.5 self-start text-sm font-medium text-ink-2 hover:text-ink"
        >
          <ChevronRight
            aria-hidden
            className={cn("size-3.5 transition-transform", open && "rotate-90")}
          />
          Look first
          <span className="font-normal text-muted">· see each one and why</span>
        </button>
        {open ? (
          <div className="flex flex-col gap-2">
            {groups.map((group) => (
              <section key={group.id} aria-label={cleanupLabel(group.label)}>
                {headed ? (
                  <h4 className="mm-kicker mb-1 !text-[10px]">{cleanupLabel(group.label)}</h4>
                ) : null}
                <ul className="flex flex-col divide-y divide-line border-y border-line">
                  {group.items.map((item) => (
                    <RoutineItemRow
                      key={item.threadKey}
                      item={item}
                      thread={group.threads[item.threadKey]}
                      out={left.has(item.threadKey)}
                      proposed={group.status === "proposed"}
                      onToggle={() => toggleItem(item.threadKey)}
                      onUnsubscribeOpen={() => setArmedAt(null)}
                    />
                  ))}
                </ul>
              </section>
            ))}
          </div>
        ) : null}
        {proposedGroups.length > 0 ? (
          armedAt ? (
            <div
              role="group"
              aria-label="Confirm archive"
              className="flex scroll-mb-6 flex-col gap-2 rounded-lg border border-accent/40 bg-accent-soft/60 px-3 py-2.5"
            >
              <p className="text-sm text-ink">
                {progress
                  ? `Archiving group ${progress.at} of ${progress.of}…`
                  : `Archive ${threadsWord(count)} in Gmail? Nothing is deleted; they stay in All Mail.`}
              </p>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs text-muted">
                  <Kbd>Enter</Kbd>
                  {active ? (
                    <>
                      {" "}
                      (or <Kbd>1</Kbd> again)
                    </>
                  ) : null}{" "}
                  to confirm · <Kbd>Esc</Kbd> to cancel
                </span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => setArmedAt(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    ref={confirmRef}
                    size="sm"
                    variant="primary"
                    busy={busy}
                    onKeyDown={(e) => {
                      if (e.repeat) e.preventDefault();
                    }}
                    onClick={() => void approve()}
                    data-testid="cleanup-confirm"
                  >
                    Confirm: archive {threadsWord(count)}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <Button
              size="sm"
              variant="primary"
              className="self-start"
              disabled={!canAct}
              aria-keyshortcuts={active ? "1" : undefined}
              onClick={arm}
            >
              Archive all {count}
              {active ? <Kbd>1</Kbd> : null}
            </Button>
          )
        ) : null}
      </div>
    </article>
  );
}

/** The compact list-column row (wide screens show the block beside the list). */
export const CleanupRow = memo(function CleanupRow({
  groups,
  id,
  selected,
  onSelect,
}: CleanupRowProps) {
  if (groups.length === 0) return null;
  const summary = cleanupSummary(groups);
  return (
    <div
      id={id}
      role="option"
      aria-selected={selected}
      onClick={onSelect}
      data-routine-group={CLEANUP_GROUP_ID}
      className={cn(
        "relative flex cursor-pointer flex-col gap-0.5 border-b border-line px-4 py-3 select-none md:px-5",
        selected ? "bg-accent-soft" : "bg-memo/60 hover:bg-sunk/50",
      )}
    >
      {selected ? (
        <span aria-hidden className="absolute inset-y-0 left-0 w-[3px] bg-accent" />
      ) : null}
      <span className="mm-kicker !text-[10px]">Routine cleanup</span>
      <p className="line-clamp-2 font-serif text-[16px] leading-snug font-semibold text-ink">
        {cleanupTitle(cleanupCount(groups))}
      </p>
      {summary ? <p className="line-clamp-2 text-sm text-muted">{summary}</p> : null}
    </div>
  );
});
